import { Injectable } from '@angular/core';
import { EmployeeFunctionGroupsDaoService } from '../employee-function-groups-dao.service';
import { EmployeeFunctionGroup } from 'src/app/_model/employee-function-group';
import {
  DbPersistenceService,
  Query,
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { EmployeeFunctionGroupsDbTableDefinition } from './employee-function-groups-db-table-definition';
import { LocalEmployeeFunctionGroupsDbTableDefinition } from './local-employee-function-groups-db-table-definition';

@Injectable()
export class EmployeeFunctionGroupsDbDaoService implements EmployeeFunctionGroupsDaoService {
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private employeeFunctionGroupsTableDefinition: EmployeeFunctionGroupsDbTableDefinition,
    private localEmployeeFunctionGroupsTableDefinition: LocalEmployeeFunctionGroupsDbTableDefinition,
  ) {}

  public async saveEmployeeFunctionGroups(entries: EmployeeFunctionGroup[]): Promise<void> {
    try {
      const db = await this.dbPersistenceService.getImsDb();
      const queries: Query[] = entries.map((entry) => ({
        statement:
          'INSERT OR REPLACE INTO ' +
          this.employeeFunctionGroupsTableDefinition.tableName +
          '(id, employeeid, functiongroupid, databag)' +
          ' VALUES (?,?,?,?)',
        parameters: [
          Number(entry.getImsId()),
          entry.employeeid,
          entry.functiongroupid,
          entry.databag ? JSON.stringify(entry.databag) : undefined,
        ],
      }));
      await this.dbPersistenceService.executeBatch(db, queries);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error saving employee function groups to db',
        error?.message,
      );
    }
  }

  public async createEmployeeFunctionGroup(entry: EmployeeFunctionGroup): Promise<EmployeeFunctionGroup> {
    try {
      const db = await this.dbPersistenceService.getImsDb();
      const query: Query = {
        statement:
          'INSERT INTO ' +
          this.localEmployeeFunctionGroupsTableDefinition.tableName +
          '(employeeid, functiongroupid, databag)' +
          ' VALUES (?,?,?)',
        parameters: [
          entry.employeeid,
          entry.functiongroupid,
          entry.databag ? JSON.stringify(entry.databag) : undefined,
        ],
      };

      await this.dbPersistenceService.executeStatement(db, query);
      const res = await this.dbPersistenceService.executeQuery(db, {
        statement: 'select last_insert_rowid() as id',
        parameters: [],
      });
      if (res && res.values) {
        entry.id.secondaryId = res.values.shift()?.id;
      }
      return entry;
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error creating local employee function group mapping',
        error?.message,
      );
      return undefined;
    }
  }

  public async getEmployeeFunctionGroups(): Promise<EmployeeFunctionGroup[]> {
    const db = await this.dbPersistenceService.getImsDb();
    const entries: EmployeeFunctionGroup[] = [];

    // Synced entries - isolated try/catch so a problem reading the local table
    // (e.g. not migrated yet) can never wipe out the already-loaded synced ones.
    try {
      const data = await this.dbPersistenceService.executeQuery(db, {
        statement: 'SELECT * FROM ' + this.employeeFunctionGroupsTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < data.values.length; i++) {
        const row = data.values[i];
        entries.push(
          EmployeeFunctionGroup.fromDto(
            {
              id: row['id'],
              employeeid: row['employeeid'],
              functiongroupid: row['functiongroupid'],
            },
            row['databag'] ? JSON.parse(row['databag']) : undefined,
          ),
        );
      }
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error reading synced employee function groups from db',
        error?.message,
      );
    }

    // Locally created mappings (added via the "show all" override until the
    // backend syncs the proper mapping back)
    try {
      const localData = await this.dbPersistenceService.executeQuery(db, {
        statement: 'SELECT * FROM ' + this.localEmployeeFunctionGroupsTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < localData.values.length; i++) {
        const row = localData.values[i];
        entries.push(
          EmployeeFunctionGroup.fromDto(
            {
              id: undefined,
              secondaryid: row['id'],
              employeeid: row['employeeid'],
              functiongroupid: row['functiongroupid'],
            },
            row['databag'] ? JSON.parse(row['databag']) : undefined,
          ),
        );
      }
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error reading local employee function groups from db',
        error?.message,
      );
    }

    return entries;
  }

  public async reset(): Promise<void> {
    // Unlike clear() (used during a sync), a Reset should wipe locally
    // created, not-yet-synced mappings too.
    const db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.localEmployeeFunctionGroupsTableDefinition.tableName,
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.localEmployeeFunctionGroupsTableDefinition.tableName,
    );
  }

  public async clear(): Promise<void> {
    // Only the synced table is cleared here - locally created mappings must
    // survive a resync until the backend actually confirms/sends them back.
    const db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.employeeFunctionGroupsTableDefinition.tableName,
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.employeeFunctionGroupsTableDefinition.tableName,
    );
  }
}
